import { motion } from "framer-motion";
import clsx from "clsx";
import { ArrowUpLeft, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "units/dashboard/context/LanguageProvider";
import { RTL_LANGUAGES } from "i18n";

export default function ShowCard({ project, index = 0 }) {
  const { t } = useTranslation("landing");
  const { lang } = useLanguage();
  const isRTL = RTL_LANGUAGES.includes(lang);

  const Arrow = isRTL ? ArrowUpLeft : ArrowUpRight;
  const { key, image, span, height, hasStat } = project;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        delay: index * 0.1,
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={clsx(
        "group relative overflow-hidden rounded-card border border-line bg-surface",
        "cursor-pointer transition-all duration-500",
        "hover:border-teal-3 hover:shadow-card-lg",
        span,
        height
      )}
    >
      {/* Image */}
      <img
        src={image}
        alt={t(`${key}.title`)}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover brightness-[0.85] transition-all duration-700 ease-snap group-hover:scale-110 group-hover:brightness-100"
      />

      {/* Overlay */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-[#0a1018]/95 via-[#0a1018]/40 to-transparent"
      />

      {/* Tag */}
      <span className="absolute top-5 end-5 z-10 rounded-full bg-white/95 px-3.5 py-1.5 text-[11.5px] font-bold text-teal backdrop-blur-md sm:text-[12px]">
        {t(`${key}.tag`)}
      </span>

      {/* Stat — فقط روی کارت اول */}
      {hasStat && (
        <div className="absolute top-5 start-5 z-10 flex items-center gap-2 rounded-xl bg-white/95 px-3.5 py-2.5 text-[11.5px] font-bold text-ink backdrop-blur-md sm:text-[12px]">
          <b className="text-[14px] text-teal sm:text-[15px]">
            {t(`${key}.statVal`)}
          </b>
          <span>{t(`${key}.statText`)}</span>
        </div>
      )}

      {/* Meta */}
      <div className="absolute inset-x-0 bottom-0 z-10 p-5 sm:p-7">
        <h3 className="mb-1.5 text-[18px] font-extrabold leading-tight text-white sm:text-[20px]">
          {t(`${key}.title`)}
        </h3>
        <p className="text-[13px] leading-[1.7] text-white/80 sm:text-[13.5px]">
          {t(`${key}.desc`)}
        </p>

        {/* Visit indicator — only on hover */}
        <div
          className={clsx(
            "mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-gold-2",
            "translate-y-2 opacity-0 transition-all duration-400 ease-snap",
            "group-hover:translate-y-0 group-hover:opacity-100"
          )}
        >
          {t("showcase.visit")}
          <Arrow className="h-3.5 w-3.5" />
        </div>
      </div>
    </motion.article>
  );
}